import React, { useState } from 'react';
import { useTournament } from '../hooks/useTournament';
import FieldCapacityNotification from './FieldCapacityNotification';

const TournamentMatrix = () => {
  const {
    tournamentMatrix,
    generateTournament,
    isValidConfiguration,
    players,
    numTeams,
    numFields,
    numRounds,
    updateTeams,
    updateFields,
    resetAll,
    getTeamPlayers,
    matchSchedule,
    getFieldValidation,
    teamColors,
    completedRounds
  } = useTournament();
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showFieldWarning, setShowFieldWarning] = useState(false);
  const [fieldValidation, setFieldValidation] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerateTournament = async () => {
    if (!isValidConfiguration()) {
      setError('Bitte füllen Sie alle erforderlichen Einstellungen aus, bevor Sie das Turnier generieren.');
      return;
    }
    
    // Check field capacity before generating
    const validation = getFieldValidation();
    if (validation.hasInsufficientFields) {
      setFieldValidation(validation);
      setShowFieldWarning(true);
      return;
    }
    
    if (await generateTournament()) {
      setError('');
      setShowFieldWarning(false);
    } else {
      setError('Turnier konnte nicht generiert werden. Bitte überprüfen Sie Ihre Einstellungen.');
    }
  };

  // Handle field capacity notification actions
  const handleReduceTeams = () => {
    const optimalTeams = numFields * 2;
    updateTeams(optimalTeams);
    setShowFieldWarning(false);
    setError('');
    setSuccessMessage('');
    setIsGenerating(true);
    
    // Provide feedback and automatically generate tournament with new settings
    setTimeout(async () => {
      // Pass the new team count directly to generateTournament to avoid state timing issues
      if (await generateTournament(false, optimalTeams, null)) {
        setError('');
        setSuccessMessage(`Teams erfolgreich auf ${optimalTeams} reduziert und Turnier neu generiert.`);
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        setError('Turnier konnte nicht generiert werden. Bitte überprüfen Sie Ihre Einstellungen.');
      }
      setIsGenerating(false);
    }, 100); // Small delay to ensure state update
  };

  const handleIncreaseFields = () => {
    const validation = getFieldValidation();
    // Calculate the correct required fields based on current team count
    const maxSimultaneousMatches = Math.floor(numTeams / 2);
    const requiredFields = maxSimultaneousMatches;
    
    updateFields(requiredFields);
    setShowFieldWarning(false);
    setError('');
    setSuccessMessage('');
    setIsGenerating(true);
    
    // Provide feedback and automatically generate tournament with new settings
    setTimeout(async () => {
      // Pass the new field count directly to generateTournament to avoid state timing issues
      if (await generateTournament(false, null, requiredFields)) {
        setError('');
        setSuccessMessage(`Felder erfolgreich auf ${requiredFields} erhöht und Turnier neu generiert.`);
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        setError('Turnier konnte nicht generiert werden. Bitte überprüfen Sie Ihre Einstellungen.');
      }
      setIsGenerating(false);
    }, 100); // Small delay to ensure state update
  };

  const handleProceedAnyway = () => {
    setIsGenerating(true);
    setShowFieldWarning(false);
    setError('');
    setSuccessMessage('');
    
    setTimeout(async () => {
      if (await generateTournament(true)) { // Use improved algorithm
        setError('');
        setSuccessMessage('Turnier mit verbessertem Algorithmus generiert - Spielverteilung wurde optimiert.');
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        setError('Turnier konnte nicht generiert werden. Bitte überprüfen Sie Ihre Einstellungen.');
      }
      setIsGenerating(false);
    }, 100);
  };

  const handleDismissWarning = () => {
    setShowFieldWarning(false);
  };

  const handleReset = () => {
    if (window.confirm('Möchten Sie wirklich alle Spieler, Einstellungen und Ergebnisse löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      resetAll();
      setError('');
    }
  };

  const { manualTeamSizes, getEffectiveTeamSizes, canAdjustPairSize, adjustPairSize, resetManualPair } = useTournament();

  // Function to find the field where a player plays in a specific round
  const getPlayerField = (playerName, roundIndex, teamLetter) => {
    if (!matchSchedule || !matchSchedule[roundIndex] || teamLetter === '-') {
      return null;
    }
    
    // Convert teamLetter to team index (A=0, B=1, etc.)
    const teamIndex = teamLetter.charCodeAt(0) - 65;
    
    // Find the match where the player's team is playing
    const match = matchSchedule[roundIndex].find(
      m => m.teamA === teamIndex || m.teamB === teamIndex
    );
    
    if (match) {
      return match.field;
    }
    
    return null;
  };

  // Helper: determine readable text color (black/white) based on background hex
  const getContrastColor = (hex) => {
    if (!hex) return '#000';
    // Remove # if present
    const h = hex.replace('#', '');
    // Parse r,g,b
    const bigint = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    // Standard luminance formula
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6 ? '#000000' : '#ffffff';
  };

  // Handler to generate print-ready schedule for a single player
  const handlePlayerSchedule = (playerName) => {
    // Build schedule data
    const playerSchedule = tournamentMatrix.map((round, roundIdx) => {
      // find player's assignment
      const assignment = round.assignments?.find(a => a.playerName === playerName);
      const teamLetter = assignment ? assignment.team : '-';
      const fieldNumber = getPlayerField(playerName, roundIdx, teamLetter) || '-';
      // Determine teammates (other players with same team in this round)
      let teammates = [];
      if (assignment && round.assignments) {
        teammates = round.assignments
          .filter(a => a.team === teamLetter && a.playerName !== playerName)
          .map(a => a.playerName);
      }
      return { round: roundIdx + 1, team: teamLetter, field: fieldNumber, teammates };
    });

    // Generate HTML for print
    const printWindow = window.open('', '_blank');
    const html = `
      <html>
        <head>
          <title>${playerName} Spielplan</title>
          <style>
            body { font-family: sans-serif; padding: 20px; }
            h1 { text-align: center; color: #333; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: center; }
            th { background-color: #f0f0f0; }
            tbody tr:nth-child(odd) { background-color: #ffffff; }
            tbody tr:nth-child(even) { background-color: #f2f6fa; }
            </style>
        </head>
        <body>
          <h1>${playerName} Spielplan</h1>
          <table>
            <thead>
              <tr><th>Runde</th><th>Feld</th><th>Team</th><th>Mitspieler</th></tr>
            </thead>
            <tbody>
              ${playerSchedule.map(item => {
                const teamLetter = item.team;
                const teamIndex = teamLetter && teamLetter !== '-' ? teamLetter.charCodeAt(0) - 65 : null;
                const teamColor = (teamIndex !== null && Array.isArray(teamColors) && teamColors[teamIndex]) ? teamColors[teamIndex] : '';
                const textColor = teamColor ? getContrastColor(teamColor) : '#000';
                const teamHtml = teamLetter && teamLetter !== '-' ? `<div style="background:${teamColor};color:${textColor};padding:6px;border-radius:6px;display:inline-block;min-width:36px;text-align:center">${teamLetter}</div>` : '-';
                const roundText = `Runde ${item.round}`;
                const fieldText = item.field !== '-' ? `Feld ${item.field}` : '-';
                return `<tr><td>${roundText}</td><td>${fieldText}</td><td>${teamHtml}</td><td>${item.teammates.map(t => `<div>${t}</div>`).join('')}</td></tr>`;
              }).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    // printWindow.close(); // optional
  };

  const handlePrintMatrix = () => {
    // Build a simple, safe HTML table for the matrix printout
    const printWindow = window.open('', '_blank');
    const html = `
      <html>
        <head>
          <title>Turnier-Matrix</title>
          <style>
            body { font-family: sans-serif; padding: 20px; }
            h1 { text-align: center; color: #333; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: center; }
            th { background-color: #f0f0f0; }
            .print-team-letter { font-weight: bold; font-size: 1.0em; }
            tbody tr:nth-child(odd) { background-color: #ffffff; }
            tbody tr:nth-child(even) { background-color: #f2f6fa; }
          </style>
        </head>
        <body>
          <h1>Turnier-Matrix</h1>
          <table>
            <thead>
              <tr>
                <th>Spieler</th>
                ${tournamentMatrix.map((_, roundIndex) => `<th>Runde ${roundIndex + 1}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${players.map(playerName => {
                const row = tournamentMatrix.map((round, roundIdx) => {
                  const assignment = round.assignments?.find(a => a.playerName === playerName);
                  const teamLetter = assignment ? assignment.team : '-';
                  const fieldNumber = getPlayerField(playerName, roundIdx, teamLetter) || '-';
                  const teamIndex = teamLetter && teamLetter !== '-' ? teamLetter.charCodeAt(0) - 65 : null;
                  const teamColor = (teamIndex !== null && Array.isArray(teamColors) && teamColors[teamIndex]) ? teamColors[teamIndex] : '';
                  const textColor = teamColor ? getContrastColor(teamColor) : '#000';
                  const teamHtml = teamLetter && teamLetter !== '-' ? `<div class="print-team-letter" style="background:${teamColor};color:${textColor};padding:6px;border-radius:6px;display:inline-block;min-width:36px;text-align:center">${teamLetter}</div>` : '<div class="print-team-letter">-</div>';
                  return `<td>${teamHtml}${fieldNumber !== '-' ? `<div style="font-size:0.85em;margin-top:4px;color:#555">F${fieldNumber}</div>` : ''}</td>`;
                }).join('');

                return `<tr><td style="font-weight:600">${playerName}</td>${row}</tr>`;
              }).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // Function to print all player schedules
  const handlePrintAllPlayerSchedules = () => {
    if (!players.length || !tournamentMatrix.length) return;

    const printWindow = window.open('', '_blank');
    
    // Generate schedule data for all players
    const allPlayerSchedules = players.map(playerName => {
      const playerSchedule = tournamentMatrix.map((round, roundIdx) => {
        const assignment = round.assignments?.find(a => a.playerName === playerName);
        const teamLetter = assignment ? assignment.team : '-';
        const fieldNumber = getPlayerField(playerName, roundIdx, teamLetter) || '-';
        let teammates = [];
        if (assignment && round.assignments) {
          teammates = round.assignments
            .filter(a => a.team === teamLetter && a.playerName !== playerName)
            .map(a => a.playerName);
        }
        return { round: roundIdx + 1, team: teamLetter, teammates, field: fieldNumber };
      });
      return { playerName, schedule: playerSchedule };
    });

    const html = `
      <html>
        <head>
          <title>Alle Spielerpläne</title>
          <style>
            body { font-family: sans-serif; padding: 20px; }
            h1 { text-align: center; color: #333; margin-bottom: 30px; }
            .player-schedule { margin-bottom: 40px; page-break-inside: avoid; }
            .player-name { font-size: 1.3em; font-weight: bold; color: #333; margin-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: center; }
            th { background-color: #f0f0f0; font-weight: bold; }
            @media print { .player-schedule { page-break-after: always; } }
            tbody tr:nth-child(odd) { background-color: #ffffff; }
            tbody tr:nth-child(even) { background-color: #f2f6fa; }
          </style>
        </head>
        <body>
          ${allPlayerSchedules.map(({ playerName, schedule }) => {
            const rows = schedule.map(item => {
              const teamLetter = item.team;
              const teamIndex = teamLetter && teamLetter !== '-' ? teamLetter.charCodeAt(0) - 65 : null;
              const teamColor = (teamIndex !== null && Array.isArray(teamColors) && teamColors[teamIndex]) ? teamColors[teamIndex] : '';
              const textColor = teamColor ? getContrastColor(teamColor) : '#000';
              const teamHtml = teamLetter && teamLetter !== '-' ? `<div style="background:${teamColor};color:${textColor};padding:6px;border-radius:6px;display:inline-block;min-width:36px;text-align:center">${teamLetter}</div>` : '-';
              const roundText = `Runde ${item.round}`;
              const fieldText = item.field !== '-' ? `Feld ${item.field}` : '-';
              // New order: Runde; Feld; Team; Mitspieler — include wording in each row
              return `
                <tr>
                  <td>${roundText}</td>
                  <td>${fieldText}</td>
                  <td>${teamHtml}</td>
                  <td>${item.teammates.map(t => `<div>${t}</div>`).join('')}</td>
                </tr>
              `;
            }).join('');

            return `
              <div class="player-schedule">
                <div class="player-name">${playerName}</div>
                <table>
                  <thead>
                    <tr><th>Runde</th><th>Feld</th><th>Team</th><th>Mitspieler</th></tr>
                  </thead>
                  <tbody>
                    ${rows}
                  </tbody>
                </table>
              </div>
            `;
          }).join('')}
        </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // Function to print all field schedules
  const handlePrintAllFieldSchedules = () => {
    if (!matchSchedule.length || !numFields) return;

    const printWindow = window.open('', '_blank');
    
    // Generate schedule data for all fields
    const allFieldSchedules = [];
    for (let fieldNumber = 1; fieldNumber <= numFields; fieldNumber++) {
      const fieldSchedule = matchSchedule.map((round, roundIdx) => {
        const match = round[fieldNumber - 1]; // fieldNumber is 1-based, array is 0-based
        if (!match) return null;
        
        const teamAPlayers = getTeamPlayers(roundIdx, match.teamA);
        const teamBPlayers = getTeamPlayers(roundIdx, match.teamB);
        
        return {
          round: roundIdx + 1,
          teamA: String.fromCharCode(65 + match.teamA), // Convert to letter
          teamB: String.fromCharCode(65 + match.teamB), // Convert to letter
          teamAPlayers: teamAPlayers,
          teamBPlayers: teamBPlayers,
          scoreA: match.scoreA,
          scoreB: match.scoreB
        };
      }).filter(item => item !== null);
      
      // Determine header labels dynamically based on first non-empty scheduled match
      let headerTeamALabel = 'Team A';
      let headerTeamBLabel = 'Team B';
      if (fieldSchedule.length > 0) {
        const first = fieldSchedule.find(m => m.teamA && m.teamB);
        if (first) {
          headerTeamALabel = first.teamA;
          headerTeamBLabel = first.teamB;
        }
      }

      allFieldSchedules.push({ fieldNumber, schedule: fieldSchedule, headerTeamALabel, headerTeamBLabel });
    }

    const html = `
      <html>
        <head>
          <title>Alle Feld Spielpläne</title>
          <style>
            body { font-family: sans-serif; padding: 20px; }
            h1 { text-align: center; color: #333; margin-bottom: 30px; }
            .field-schedule { margin-bottom: 40px; page-break-inside: avoid; }
            .field-name { font-size: 1.3em; font-weight: bold; color: #333; margin-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: center; }
            th { background-color: #f0f0f0; font-weight: bold; }
            .team-section { text-align: left; padding: 4px; }
            .team-name { font-weight: bold; font-size: 1.1em; margin-bottom: 4px; }
            .players-list { font-size: 0.9em; color: #555; }
            .score-cell { font-weight: bold; font-size: 1.2em; }
            @media print { .field-schedule { page-break-after: always; } }
            tbody tr:nth-child(odd) { background-color: #ffffff; }
            tbody tr:nth-child(even) { background-color: #f2f6fa; }
          </style>
        </head>
        <body>
          ${allFieldSchedules.map(({ fieldNumber, schedule, headerTeamALabel, headerTeamBLabel }) => `
            <div class="field-schedule">
              <div class="field-name">Feld ${fieldNumber}</div>
              <table>
                <thead>
                  <tr>
                    <th>Runde</th>
                    <th>${(() => {
                      const idx = headerTeamALabel && headerTeamALabel.length === 1 ? headerTeamALabel.charCodeAt(0) - 65 : null;
                      const color = (idx !== null && Array.isArray(teamColors) && teamColors[idx]) ? teamColors[idx] : '';
                      const text = color ? getContrastColor(color) : '#000';
                      return color ? `<span style="background:${color};color:${text};padding:6px;border-radius:6px;display:inline-block;min-width:36px;text-align:center">${headerTeamALabel}</span>` : headerTeamALabel;
                    })()}</th>
                    <th>${(() => {
                      const idx = headerTeamBLabel && headerTeamBLabel.length === 1 ? headerTeamBLabel.charCodeAt(0) - 65 : null;
                      const color = (idx !== null && Array.isArray(teamColors) && teamColors[idx]) ? teamColors[idx] : '';
                      const text = color ? getContrastColor(color) : '#000';
                      return color ? `<span style="background:${color};color:${text};padding:6px;border-radius:6px;display:inline-block;min-width:36px;text-align:center">${headerTeamBLabel}</span>` : headerTeamBLabel;
                    })()}</th>
                    <th>Ergebnis</th>
                  </tr>
                </thead>
                <tbody>
                  ${schedule.map(item => {
                    // Color the team header cell in the first row by using header labels
                    const teamAletter = item.teamA;
                    const teamBletter = item.teamB;
                    const teamAIndex = teamAletter ? teamAletter.charCodeAt(0) - 65 : null;
                    const teamBIndex = teamBletter ? teamBletter.charCodeAt(0) - 65 : null;
                    const teamAColor = (teamAIndex !== null && Array.isArray(teamColors) && teamColors[teamAIndex]) ? teamColors[teamAIndex] : '';
                    const teamBColor = (teamBIndex !== null && Array.isArray(teamColors) && teamColors[teamBIndex]) ? teamColors[teamBIndex] : '';
                    const teamAText = teamAColor ? ` style="background:${teamAColor};color:${getContrastColor(teamAColor)};padding:6px;border-radius:6px;display:inline-block;min-width:36px;text-align:center"` : '';
                    const teamBText = teamBColor ? ` style="background:${teamBColor};color:${getContrastColor(teamBColor)};padding:6px;border-radius:6px;display:inline-block;min-width:36px;text-align:center"` : '';
                    return `
                    <tr>
                      <td>${item.round}</td>
                      <td class="team-section">
                        <div class="players-list">${item.teamAPlayers.map(p => `<div>${p}</div>`).join('')}</div>
                      </td>
                      <td class="team-section">
                        <div class="players-list">${item.teamBPlayers.map(p => `<div>${p}</div>`).join('')}</div>
                      </td>
                      <td class="score-cell">
                        ${item.scoreA !== null && item.scoreB !== null ? `${item.scoreA} : ${item.scoreB}` : '-'}
                      </td>
                    </tr>
                  `;
                  }).join('')}
                </tbody>
              </table>
            </div>
          `).join('')}
        </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // Main component render
  return (
    <div className="section">
      <div className="section-header">
        <h2>Turnier-Matrix</h2>
      </div>

  {/* Team summary removed to avoid duplication; managed in TeamSettings */}

      {/* Field capacity notification */}
      {showFieldWarning && fieldValidation && (
        <FieldCapacityNotification
          validation={fieldValidation}
          onReduceTeams={handleReduceTeams}
          onIncreaseFields={handleIncreaseFields}
          onProceedAnyway={handleProceedAnyway}
          onDismiss={handleDismissWarning}
        />
      )}

      {/* Tournament controls */}
      <div className="tournament-controls">
        <button 
          className="primary" 
          onClick={handleGenerateTournament}
          disabled={!isValidConfiguration() || isGenerating}
        >
          {isGenerating ? 'Turnier wird generiert...' : 'Turnier generieren'}
        </button>
        
        {tournamentMatrix.length > 0 && (
          <>
            <button 
              className="secondary" 
              onClick={handlePrintMatrix}
            >
              Turniermatrix ausdrucken
            </button>
            
            <button 
              className="secondary" 
              onClick={handlePrintAllPlayerSchedules}
            >
              Alle Spielerpläne ausdrucken
            </button>
            
            <button 
              className="secondary" 
              onClick={handlePrintAllFieldSchedules}
            >
              Alle Feldspielpläne ausdrucken
            </button>
          </>
        )}
        
        <button 
          className="danger" 
          onClick={handleReset}
        >
          Alles zurücksetzen
        </button>
      </div>

      {/* Error message */}
      {error && <div className="error-message">{error}</div>}
      
      {/* Success message */}
      {successMessage && (
        <div className="success-message" style={{ backgroundColor: '#e8f5e8', color: '#2e7d32', padding: '12px', borderRadius: '5px', textAlign: 'center', marginTop: '10px' }}>
          ✅ {successMessage}
        </div>
      )}
      
      {/* Loading message */}
      {isGenerating && (
        <div className="info-message" style={{ backgroundColor: '#e3f2fd', color: '#1976d2', padding: '12px', borderRadius: '5px', textAlign: 'center' }}>
          🔄 Turnier wird generiert...
        </div>
      )}

      {/* Tournament matrix display */}
      {tournamentMatrix.length > 0 && (
        <div className="matrix-container">
          <table className="tournament-table tournament-matrix-table striped-table">
            <thead>
              <tr>
                <th className="player-header">Spieler</th>
                {tournamentMatrix.map((_, roundIndex) => (
                  <th
                    key={roundIndex}
                    className={`round-header ${completedRounds[roundIndex] ? 'round-completed' : ''}`}
                  >
                    Runde {roundIndex + 1}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {players.map((playerName, playerIndex) => (
                <tr key={playerIndex}>
                  <td 
                    className="player-name clickable" 
                    onClick={() => handlePlayerSchedule(playerName)}
                    title="Klicken, um Spielplan für diesen Spieler auszudrucken"
                  >
                    {playerName}
                  </td>
                  {tournamentMatrix.map((round, roundIndex) => {
                    const assignment = round.assignments?.find(
                      a => a.playerName === playerName
                    );
                    const teamLetter = assignment ? assignment.team : '-';
                    const fieldNumber = getPlayerField(playerName, roundIndex, teamLetter);
                    
                    // Compute team color if available
                    const teamIndex = teamLetter && teamLetter !== '-' ? teamLetter.charCodeAt(0) - 65 : null;
                    const teamColor = (teamIndex !== null && Array.isArray(teamColors) && teamColors[teamIndex]) ? teamColors[teamIndex] : null;
                    const textColor = teamColor ? getContrastColor(teamColor) : undefined;
                    const contentStyle = teamColor ? { backgroundColor: teamColor, color: textColor, padding: '6px', borderRadius: '6px', display: 'inline-block', minWidth: '36px', textAlign: 'center' } : {};

                    return (
                      <td key={roundIndex} className={`team-cell ${completedRounds[roundIndex] ? 'round-completed' : ''}`}>
                        <div className="team-cell-content">
                          <span className="team-letter" style={contentStyle}>{teamLetter}</span>
                          {fieldNumber && (
                            <span className="field-indicator" style={{ marginLeft: '6px', fontSize: '0.85em' }}>F{fieldNumber}</span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Info message when no tournament is generated */}
      {tournamentMatrix.length === 0 && (
        <p className="info-text">
          Generieren Sie ein Turnier, um die Spieler-Matrix anzuzeigen.
        </p>
      )}
    </div>
  );
};

export default TournamentMatrix;